import { Button, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { FlatList, ListRenderItemInfo, View } from "react-native";
import { Plan } from "./utils";
import Card from "./PlanCardComponent";
import styled from "styled-components/native";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { VDBIcons } from "@utils/VDBIcons";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import ContactUsModal from "@components/molecules/ContactUs";
import { DefaultTheme } from "styled-components/native";

const ManagePlanStep1 = ({
  plansData,
  selectedPlanIndex,
  handleSelectPlan,
  additionalUsers,
  setAdditionalUsers,
  selectedPlan,
  t,
  typography,
  theme,
  trackButtonEvent,
  setShowContactUsModal,
  showContactUsModal,
  setStep,
  disableReviewButton,
}: {
  plansData: any;
  selectedPlanIndex: number;
  handleSelectPlan: (index: number) => void;
  additionalUsers: number;
  setAdditionalUsers: (users: number) => void;
  selectedPlan: Plan;
  t: any;
  typography: any;
  theme: any;
  trackButtonEvent: (
    event: string,
    component: string,
    data: any,
    view: string
  ) => void;
  setShowContactUsModal: (show: boolean) => void;
  showContactUsModal: boolean;
  setStep: (step: number) => void;
  disableReviewButton: boolean;
}) => {
  return (
    <Overlay>
      <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
        {t("managePlan.popUp.billing.title.text", "Billing Term")}
      </VDBText>
      <Spacer size={SpaceType.ExtraSmall} />
      <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
        {t(
          "managePlan.popUp.billing.description.text",
          "Billing will begin after the trial period ends"
        )}
      </VDBText>
      <Spacer size={SpaceType.Big} />
      <FlatList
        data={plansData?.plans}
        keyExtractor={(_, idx) => String(idx)}
        scrollEnabled={false}
        {...(!theme.isMobile
          ? {
              ItemSeparatorComponent: () => <Spacer size={SpaceType.Basic} />,
            }
          : {
              ItemSeparatorComponent: () => <Spacer size={SpaceType.Double} />,
            })}
        style={{ width: "100%" }}
        contentContainerStyle={{
          flexDirection: theme.isMobile ? "column" : "row",
          width: "100%",
          gap: theme.isMobile ? 0 : SpaceType.Double,
          marginBottom: theme.isMobile ? SpaceType.large : 0,
        }}
        CellRendererComponent={({ style, ...props }) => (
          <View {...props} style={[style, { flex: 1 }]} />
        )}
        renderItem={({ item, index }: ListRenderItemInfo<Plan>) => (
          <Card
            key={item.plan_id}
            title={item.label}
            description={t(
              "managePlan.popUp.promo.text",
              "⭐️ Limited-time promotion "
            )}
            checked={selectedPlanIndex === index}
            onSelect={() => handleSelectPlan(index)}
            item={item as Plan}
            additionalUsers={additionalUsers}
            setAdditionalUsers={setAdditionalUsers}
            disabled={item.plan_id === selectedPlan?.plan_id}
          />
        )}
      />
      {!theme.isMobile && <Spacer size={SpaceType.Big} />}
      <VDBText
        type={typography["Body-R"]}
        color={theme["core-06"]}
        textAlign="center"
      >
        {t(
          "managePlan.popUp.billing.screenText.text",
          "Locked users and pricing for 1 year. All users share one billing cycle. \n Additional users are prorated to match your plan term."
        )}
      </VDBText>
      <Spacer size={SpaceType.Big} />
      <Button
        title={t("managePlan.popUp.billing.button.text", "Continue")}
        category={ButtonCategory.default}
        type={ButtonType.primary}
        onPress={() => {
          setStep(2);
        }}
        iconSource={VDBIcons.RIGHT}
        iconColor={theme["primary-cont"]}
        iconSize={24}
        showIcon
        disabled={selectedPlanIndex === null || disableReviewButton}
      />
      <Spacer size={SpaceType.Big} />
      <ContactUsContainer>
        <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
          {t("managePlan.popUp.contactUs.text", "Need help?")}{" "}
        </VDBText>
        <VDBText
          type={typography["Body-M"]}
          color={theme["link-static"]}
          onPress={() => {
            trackButtonEvent(
              EventName.BUTTON_CLICK,
              EventType.CONTACT_US_BUTTON_CLICK,
              { component_type: ViewType.POP_UP },
              ViewTypeEnum?.MANAGE_PLAN_POP_UP
            );
            setShowContactUsModal(true);
          }}
        >
          {t("contactUs.title.text")}
        </VDBText>

        <ContactUsModal
          isOpen={showContactUsModal}
          onClose={() => setShowContactUsModal(false)}
          screenName="Manage Plan"
        />
      </ContactUsContainer>
    </Overlay>
  );
};

const Overlay = styled.View`
  padding-vertical: ${SpaceType.Big}px;
  padding-horizontal: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? SpaceType.Basic : SpaceType.Big}px;
  align-items: center;
`;

const ContactUsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

export default ManagePlanStep1;
